<script>
  import { onMount } from "svelte";

  import SvelteTable from "../../Components/Common/SvelteTable.svelte";
  import TableToggle from "../../Components/Common/TableToggle.svelte";
  import {
        user,
  } from "../../stores";

  import { PageHead, PageLoading } from "../../Components/Common/index.svelte";

  import { apiMethods } from "../../Services/index.svelte";

  import {
    goBack,
    getText,
    getFormatDate,
    getBoolean,
    getAmountInCurrency,
    showError,
    goToURL,
  } from "../../Utils/utilFunctions.svelte";

  import { isEnvNewOrApp } from "../../Utils/utilRoute.svelte";
  import CardLayout from "../../Components/Common/CardLayout.svelte";
  import Loader from "../../Components/Common/Loader.svelte";
  import SearchInput from "../../Components/Common/SearchInput.svelte";
  import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

  export let params = {};

  let isLoading = false,
    isLoadingRebates = false,
    insuranceFundDetails = {},
    maxGaps = [],
    fundItemsRebates = [],
    page = 0,
    pageSize = 25,
    count = 0;

  let filters = {
    itemNum: null,
    description: null,
    anaesthesiaItems: true
  }

  $: isOverseasDisplayed =
        ($user.role == 100 || $user.role == 80 || $user.role == 20) && isEnvNewOrApp();

  $: if (params.id && insuranceFundDetails.id) isLoading = false;

  const columns = [
    {
      key: "state",
      title: "State",
      headerClass: "text-left pl-3 font-bold",
      class: "text-left px-3 text-left",
      value: (v) => getText(v.state),
    },
    {
      key: "gapType",
      title: "Gap Type",
      headerClass: "text-left pl-3 font-bold",
      class: "text-left px-3 text-left",
      value: (v) => getText(v.gapType),
    },
    {
      key: "maxGap",
      title: "Max Gap",
      headerClass: "text-left pl-3 font-bold",
      class: "text-left px-3 text-left",
      value: (v) => getAmountInCurrency(v.maxGap),
    },
    {
      key: "feeType",
      title: "Fee Type",
      headerClass: "text-left pl-3 font-bold",
      class: "text-left px-3 text-left",
      value: (v) => getText(v.feeType),
    },
    {
      key: "eclipseApi",
      title: "Eclipse Api",
      headerClass: "text-left px-3 font-bold rounded-tr-xl",
      class: "text-left px-3",
      value: (v) => getText(v.eclipseApi),
    },
  ];

  const rebatesColumns = [
    {
      key: "itemNum",
      title: "Number",
      headerClass: "text-left pl-3 font-bold w-[120px] max-w-[120px]",
      class: "text-left px-3 w-[120px] max-w-[120px] whitespace-nowrap overflow-hidden text-ellipsis",
      value: (v) => `<u class="cursor-pointer">${getText(v.itemNum)}</u>`,
    },
    {
      key: "itemDescription",
      title: "Description",
      headerClass: "text-left pl-3 font-bold",
      class: "text-left px-3 text-left max-w-[500px] truncate",
      value: (v) => `<div class="truncate cursor-pointer" title='${getText(v.description)}'>${getText(v.description)}</div>`,
    } 
  ]

  onMount(async () => {
    if (params.id) {
      await getInsuranceFundDetails(params.id);
    }
  });

  const getInsuranceFundDetails = async (id) => {
    isLoading = true;

    // get fund via api because maxGaps not returning from bundle get funds call
    await apiMethods.getMethod
      .getInsuranceFundById(id)
      .then(async (res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to load this insurance fund details, please refresh the page and try again and contact support if the issue persists.",
            goBack,
          );
        else {
          insuranceFundDetails = { ...res };
          maxGaps = res.maxGaps;

          await getMbsRebatesForFund({ ...res }, page, pageSize, true);
        }
      })
      .catch((err) => {
        showError(err);
      });

    isLoading = false;
  };

  const getMbsRebatesForFund = async (fundDetails, start, limit, withLoading = false) => {
    try {
      if(withLoading)
        isLoadingRebates = true;

      var getFilters = {
        "filter[itemNum]": filters.itemNum ? filters.itemNum : "*"
      }
      if(filters.description){
        getFilters = {
          ...getFilters,
          "filter[description]": filters.description
        }
      };
      if(filters.anaesthesiaItems){
         getFilters = {
          ...getFilters,
          "filter[infoType]": "*"
        }
      }

      const existingColumnKeys = new Set(rebatesColumns.map((c) => c.key));

      var res = await apiMethods.getMethod.getMBSItems(start, limit, getFilters);
      if (res.results?.length) {
        fundItemsRebates = res.results.map((item) => {
          // build base row
          const row = {
            itemNum: item.itemNum,
            description: item.description,
          };

          // apply rebate matches: adds row.rebate_* props + pushes new columns
          applyFundRebateMatches(
            item,
            row,
            fundDetails.groupName,
            fundDetails.hcpCode,
            rebatesColumns,
            existingColumnKeys
          );

          return row;
        });

        count = res.total;
      } else {
        //no data returned
        fundItemsRebates = [];
        count = 0;
      }

      if(withLoading)
        isLoadingRebates = false;
    }
    catch(err) {
      console.log(err);
    }
  }

  const fundCodeRebateMapping = {
    //this structure, stores which properties to show for each fund hcpCode and group
    // "-" means default, if there is no exact mapping, it will use the default properties to display
    //"rebateTFS*", "rebateFAI*", "rebateAHSA*" means it will take all properties starting with the value before * (needed for state based rebates)

    //ahsa group
    "AHSA": {
      "HBF": ["rebateAHSA*", "rebateHBFWAChoice", "rebateHBFWAFully"],
      "-": ["rebateAHSA*"]
    },
    //other groups
    "*": {
      "NIB": ["rebateNIBKnownGap", "rebateNIBNoGap"],
      "SLM": ["rebateSLM"],
      "AHM": ["rebateAHM"],
      "BUP": ["rebateBUPKnownGap", "rebateBUPNoGap"],
      "CDH": ["rebateCDH"],
      "LHS": ["rebateLHS"],
      "FAI": ["rebateFAI*"],
      "HCF": ["rebateHCFKnownGap", "rebateHCFNoGap"],
      "MBP": ["rebateMBP"],
      "MDH": ["rebateMDH"],
      "RTE": ["rebateTFS*"],
      "TFS": ["rebateTFS*"],
      "-": ["benefit75", "benefit85", "benefit100"] //default properties
    }
  }
    
  const toDisplayKey = (key) => {
    if (!key) return key;

    const cleanedKey = key.toLowerCase().startsWith("rebate")
      ? key.slice(6)
      : key;

    return cleanedKey.charAt(0).toUpperCase() + cleanedKey.slice(1);
  };

  const formatValue = (v) => {
    if (v === null || v === undefined) return '';

    if (typeof v === 'number') {
      return v.toFixed(2);
    }

    return String(v);
  };

  const applyFundRebateMatches = (
    rawItem,
    targetRow,
    groupName,
    hcpCode,
    rebatesColumns,
    existingColumnKeys
  ) => {
    const groupMapping =
      fundCodeRebateMapping[groupName] || fundCodeRebateMapping["*"] || {};

    const mappingKeys =
      groupMapping[hcpCode] || groupMapping["-"] || [];

    const seen = new Set();

    for (const mapKey of mappingKeys) {
      const isWildcard = mapKey.endsWith("*");
      const prefix = isWildcard ? mapKey.slice(0, -1) : mapKey;
      if (!prefix) continue;

      if (isWildcard) {
        for (const k of Object.keys(rawItem)) {
          if (k.startsWith(prefix) && !seen.has(k) && rawItem[k] != null) {
            seen.add(k);
            addMatch(k, rawItem[k]);
          }
        }
      } else {
        if (!seen.has(prefix) && rawItem[prefix] != null) {
          seen.add(prefix);
          addMatch(prefix, rawItem[prefix]);
        }
      }
    }

    function addMatch(originalKey, value) {
      // Avoid collisions with existing row keys
      const newKey = `rebate_${originalKey}`;

      // put new property on the row you're sending to the table
      targetRow[newKey] = value;

      // add column only once
      if (!existingColumnKeys.has(newKey)) {
        existingColumnKeys.add(newKey);

        rebatesColumns.push({
          key: newKey,
          title: toDisplayKey(originalKey), // "RebateAHSAVIC" -> "AHSAVIC"
          headerClass: "text-left pl-3 font-bold w-[120px] max-w-[120px]",
          class:
            "text-left px-3 w-[120px] max-w-[120px] whitespace-nowrap overflow-hidden text-ellipsis",
          value: (row) =>
            row[newKey] == null ? "" : getText(`$${formatValue(row[newKey])}`),
        });
      }
    }
  };

  const handleFilterRebates = () => {
    //always reset the page
    page = 0;

    getMbsRebatesForFund({ ...insuranceFundDetails }, page, pageSize, false);

  };

  const getEraMatch = (data) => {
    if (data && data.length && data.indexOf(";")) {
      return data.replaceAll(";", ",;").split(";");
    }
    return [data];
  };

  const onPageChange = (e) => {
      page = Number(e.detail.page);

      getMbsRebatesForFund({ ...insuranceFundDetails }, Number(e.detail.firstDataIndex), pageSize);
  };

  const onPageSizeChange = (newPageSize) => {
      pageSize = newPageSize;
      page = 0;

      getMbsRebatesForFund({ ...insuranceFundDetails }, 0, newPageSize);
  };

  const handleClickCell = (
      { target: { className, classList } },
      rowData,
      key,
  ) => {
      if (key == "itemNum") {
          goToURL(`/MBSItems/view/${rowData.itemNum}`)
      }
  };

</script>

{#if isLoading}
  <PageLoading />
{:else}
  <PageHead title="View insurance fund details" />
  

  <div class="flex-1 relative mt-4">
    <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4 px-2">
      <CardLayout title="Insurance Fund Details">
        <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
            <div class="flex flex-col flex-1 gap-y-4 h-full">
              <div class="flex flex-row gap-x-2">
                <p class="title">Name:</p>
                <p class="content">
                  {getText(insuranceFundDetails.name)}
                </p>
              </div>

              <div class="flex flex-row gap-x-2">
                <p class="title">Eclipse Code:</p>
                <p class="content">
                  {getText(insuranceFundDetails.eclipseCode)}
                </p>
              </div>

              <div class="flex flex-row gap-x-2">
                <p class="title">HCP Code:</p>
                <p class="content">{getText(insuranceFundDetails.hcpCode)}</p>
              </div>

              <!-- <div class="flex flex-row gap-x-2">
                <p class="title">Is No-Gap Program:</p>
                <p class="content">
                  <TableToggle 
                    valueKey= "isNoGapProgram",
                    row={insuranceFundDetails}
                    clickFunction={async (insuranceFund, e) => {
                        e.stopPropagation();
                        insuranceFund.isNoGapProgram = !insuranceFund.isNoGapProgram;
                        await apiMethods.putMethod
                            .updateInsuranceFundIsGapProgram(insuranceFund.id, insuranceFund.isNoGapProgram ? 1 : 0)
                            .then((res) => {
                                if (res.error) showError(res.error);
                                getInsuranceFundDetails(insuranceFund.id);
                            });
                    }}>
                  </TableToggle>
                </p>
              </div> -->
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
              <div class="flex flex-row gap-x-2">
                <p class="title">Location ID:</p>
                <p class="content">
                  {getText(insuranceFundDetails.locationId)}
                </p>
              </div>

              <div class="flex flex-row gap-x-2">
                <p class="title">is AHSA:</p>
                <p class="content">{getBoolean(insuranceFundDetails.isAHSA)}</p>
              </div>

              <!-- <div class="flex flex-row gap-x-2">
                <p class="title">Source:</p>
                <p class="content">{getText(insuranceFundDetails.source)}</p>
              </div> -->

              <div class="flex flex-row gap-x-2">
                <p class="title">ERA Match:</p>
                <div class="flex flex-col gap-y-1">
                  {#each getEraMatch(insuranceFundDetails.eraMatch) as Ematch}
                    <p class="content">{Ematch}</p>
                  {/each}
                </div>
              </div>
              {#if isOverseasDisplayed}
                <div class="flex flex-row gap-x-2">
                  <p class="title">Overseas insurance enabled:</p>
                  <p class="content">
                    <TableToggle 
                      valueKey= "isOverseas",
                      row={insuranceFundDetails}
                      clickFunction={async (insuranceFund, e) => {
                          e.stopPropagation();
                          insuranceFund.isOverseas = !insuranceFund.isOverseas;
                          await apiMethods.putMethod
                              .updateInsuranceFundIsOverseas(insuranceFund.id, insuranceFund.isOverseas ? 1 : 0)
                              .then((res) => {
                                  if (res.error) showError(res.error);
                                  getInsuranceFundDetails(insuranceFund.id);
                              });
                      }}>
                    </TableToggle>
                  </p>
                </div>
              {/if}
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
              <div class="flex flex-row gap-x-2">
                <p class="title">Created Date:</p>
                <p class="content">
                  {getFormatDate(insuranceFundDetails.createdDate)}
                </p>
              </div>

              <div class="flex flex-row gap-x-2">
                <p class="title">Updated Date:</p>
                <p class="content">
                  {getFormatDate(insuranceFundDetails.updatedDate)}
                </p>
              </div>
            </div>
        </div>

      </CardLayout>
      <CardLayout title = "Max Gaps Items">
          {#if maxGaps.length}
            <SvelteTable {columns} rows={maxGaps} />
          {:else}
            <div class="grid flex-1 h-full card bg-white rounded-box">
              <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>No Max Gaps Info</p>
              </div>
            </div>
          {/if}
      </CardLayout>
      <CardLayout title = "Rebates" cardClass="max-h-[600px]">
        {#if isLoadingRebates}
          <div class="flex flex-1 gap-x-4 items-center p-4">
            <Loader />
            <p>Fetching rebates</p>
          </div>
        {:else}
          <div class="flex flex-col gap-y-2 h-[100%]">
            <div class="p-4 flex flex-row gap-2">
              <SearchInput
                  bind:value={filters.itemNum}
                  placeholder="Search by item num"
                  inputClass="!w-full bg-transparent hover:bg-transparent"
                  inputFunc={handleFilterRebates}
                  isLong
              />
              <div class="flex-1">
                <SearchInput
                  bind:value={filters.description}
                  placeholder="Search by item description"
                  inputClass="!w-full bg-transparent hover:bg-transparent"
                  inputFunc={handleFilterRebates}
                  isLong
                />
              </div>
              <div class="flex flex-row gap-x-[6px]">
              <input
                      type="checkbox"
                      class="toggle toggle-secondary"
                      bind:checked={filters.anaesthesiaItems}
                      id="disabledComms"
                      on:change={handleFilterRebates}
                      disabled={isLoadingRebates}
                  />
                  <label for="disabledComms">Only anaesthesia items</label>
              </div>
              
            </div>
            <div class="flex flex-1 overflow-scroll">
              {#if fundItemsRebates.length}
                <SvelteTable 
                  columns={rebatesColumns} 
                  rows={fundItemsRebates}
                  clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
                />
              {:else}
                <div class="grid flex-1 h-full card bg-white rounded-box">
                  <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                    <p>No Rebates Info </p>
                  </div>
                </div>
              {/if}
            </div>
            <div class="p-2">
              <TableFooter
                  {page}
                  {pageSize}
                  {count}
                  showSelected
                  selectedCount={fundItemsRebates.length}
                  tableData={fundItemsRebates}
                  {onPageChange}
                  {onPageSizeChange}
              />
            </div>
          </div>
        {/if}
      </CardLayout>
    </div>
  </div>
{/if}

<style>
 
</style>
